// @noflow
import Bariol from '~/assets/fonts/bariol_regular.ttf';
import { colorProptype } from '~/core/utils/custom-prop-types';
import PropTypes from 'prop-types';
import * as React from 'react';
import theme from '~/core/themes/main';

const FONT_DEF = 'Arial, sans-serif';
const FONT_SIZE_COEF = 0.35;
const USABLE_HEIGHT_COEF = 0.65;

/*** Helper functions ***/

const _getYPosAccordingToRatio = (height, ratio) => height / 2 * (1 + Math.sin(ratio * Math.PI) * USABLE_HEIGHT_COEF);

const _getFactorAccordingToRatio = ratio => (Math.cos(ratio * Math.PI) + 1) / 2;

const _getColor = color => `rgb(${color.r}, ${color.g}, ${color.b})`;

const _getColorWithOpacity = (color, opacity) => `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`;

const _getStyleAccordingToRangeColor = (displayFloatIndex, rangeColors) => {
    // when a float index is between 2 colors it will generated a colour which is propotional to the index value
    // e.g. displayFloatIndex = 1.25 will have 75% from rangeColours[1] and 25% from rangeColours[2]
    const colorOffset = displayFloatIndex % 1;
    const colorIndex = Math.max(Math.floor(displayFloatIndex), 0);
    const thisColor = rangeColors[colorIndex];
    let nextColor = rangeColors[colorIndex + 1];
    if (!nextColor) {
        nextColor = thisColor;
    }
    const calculateColor = {
        r: Math.round(thisColor.r + (nextColor.r - thisColor.r) * colorOffset),
        g: Math.round(thisColor.g + (nextColor.g - thisColor.g) * colorOffset),
        b: Math.round(thisColor.b + (nextColor.b - thisColor.b) * colorOffset)
    };
    return _getColor(calculateColor);
};

const _normalizeValue = value => (typeof value === 'object' ? value : [{ text: value, coef: 1 }]);

const _calculateTextWidth = (ctx, font, fontSize, v) => {
    ctx.font = `${fontSize * v.coef}px ${v.font || font || FONT_DEF}`;
    return ctx.measureText(v.text).width;
};

const _displayValue = (ctx, font, fontSize, value, xPosition, y) => {
    ctx.font = `${fontSize * value.coef}px ${value.font || font || FONT_DEF}`;
    ctx.fillText(value.text, xPosition, y + fontSize / 3);
};

/*** Component ***/

export default class SpinnerValue extends React.Component {
    static propTypes = {
        bgColor: colorProptype,
        color: colorProptype,
        ctx: PropTypes.object,
        displayFloatIndex: PropTypes.number,
        font: PropTypes.string,
        height: PropTypes.number,
        isLoop: PropTypes.bool,
        rangeColors: PropTypes.array,
        rangeValues: PropTypes.array,
        visibilityCoef: PropTypes.number,
        width: PropTypes.number
    };

    state = {
        fontLoaded: false
    };

    static defaultProps = {
        bgColor: { r: 180, g: 180, b: 180 },
        color: { r: 255, g: 255, b: 255 },
        isLoop: false,
        rangeValues: [],
        visibilityCoef: 1.8
    };

    async componentDidMount() {
        const { loadFont = true } = this.props;

        this._mounted = true;

        if (loadFont && window.FontFace) {
            const myFont = new FontFace('Bariol', `url(${Bariol})`);
            await myFont.load();

            if (!this._mounted) {
                return;
            }
        }

        this.setState({
            fontLoaded: true
        });
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    componentDidUpdate() {
        const {
            bgColor,
            ctx,
            height,
            displayFloatIndex,
            isLoop,
            rangeColors,
            rangeValues,
            visibilityCoef,
            width,
            disabled
        } = this.props;

        const { fontLoaded } = this.state;

        // don't do anything until the ctx is loaded
        if (!ctx || !fontLoaded) {
            return null;
        }

        ctx.fillStyle = disabled
            ? theme.mediumGrey
            : rangeColors ? _getStyleAccordingToRangeColor(displayFloatIndex, rangeColors) : _getColor(bgColor);

        ctx.fillRect(0, 0, width, height);

        const rangeToFilter = isLoop ? [...rangeValues, ...rangeValues, ...rangeValues] : rangeValues;

        const valuesToShow = rangeToFilter
            .map((value, index) => ({
                value,
                index: isLoop ? index - rangeValues.length : index
            }))
            .filter(o => Math.abs(displayFloatIndex - o.index) < visibilityCoef);

        valuesToShow.forEach(o => {
            this.displayValue((o.index - displayFloatIndex) / visibilityCoef, o.value);
        });
    }

    displayValue = (ratio, value) => {
        const { color, ctx, font, height, width } = this.props;

        const y = _getYPosAccordingToRatio(height, ratio);
        const factor = _getFactorAccordingToRatio(ratio);

        const fontSize = factor * height * FONT_SIZE_COEF;

        const normalizedValue = _normalizeValue(value);
        const widths = normalizedValue.map(value => _calculateTextWidth(ctx, font, fontSize, value));
        const fullTextWidth = widths.reduce((a, b) => a + b);

        ctx.fillStyle = _getColorWithOpacity(color, factor);
        let xPosition = (width - fullTextWidth) / 2;
        normalizedValue.forEach((v, i) => {
            _displayValue(ctx, font, fontSize, v, xPosition, y);
            xPosition += widths[i];
        });
    };

    render() {
        return null;
    }
}



// WEBPACK FOOTER //
// ./src/components/widgets/spinner/spinner-value/spinner-value.component.js